import { useAppStore } from '@/lib/app-store'
import { cn } from '@/lib/utils'
import { useLingui } from '@lingui/react/macro'
import { setCookie, deleteCookie } from '@/lib/cookies'

export function ThemeButton(props: { className?: string; style?: React.CSSProperties }) {
  const { store, setStore } = useAppStore()
  const { t } = useLingui()

  const handleThemeToggle = () => {
    const currentTheme = store.resolvedDarkMode
    const newTheme = currentTheme === 'dark' ? 'light' : 'dark'

    // If the chosen appearance already matches the OS preference, the user isn't really "forcing" a
    // theme — so DON'T persist a cookie. Drop any existing one and revert to system mode, so an
    // untouched-relative-to-OS choice leaves no stored preference (and the app keeps following the OS).
    // Only an appearance that differs from the OS gets pinned with a cookie.
    const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
    const matchesSystem = (newTheme === 'dark') === systemPrefersDark

    if (matchesSystem) {
      deleteCookie('theme')
      setStore({ darkMode: 'system' })
    } else {
      setCookie('theme', newTheme)
      setStore({ darkMode: newTheme })
    }

    // Apply dark class to document (newTheme is the resolved appearance in either branch)
    if (newTheme === 'dark') {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
  }

  return (
    <button
      type="button"
      onClick={handleThemeToggle}
      className={cn('cursor-pointer rounded-[5px] px-3.5 py-2.5 transition-opacity hover:opacity-70', props.className)}
      style={props.style}
      aria-label={store.resolvedDarkMode === 'dark' ? t`Enable dark mode` : t`Disable dark mode`}
    >
      <div
        className={`relative h-6 w-6 rounded-full transition-transform duration-400 ${
          store.resolvedDarkMode === 'dark'
            ? 'scale-[0.3] rotate-0 shadow-[inset_32px_-32px_0_0_rgb(var(--text))]'
            : 'scale-[0.5] -rotate-2 shadow-[inset_8px_-8px_0_0_rgb(var(--text))]'
        }`}
      >
        <div
          className={`absolute top-1/2 left-1/2 h-2 w-2 -translate-x-1 -translate-y-1 rounded-full transition-[transform,width,height] duration-350 ${
            store.resolvedDarkMode === 'dark' ? 'scale-100 opacity-100' : 'scale-0 opacity-0'
          }`}
          style={{
            boxShadow: `
            0 -23px 0 rgb(var(--text)),
            0 23px 0 rgb(var(--text)),
            23px 0 0 rgb(var(--text)),
            -23px 0 0 rgb(var(--text)),
            15px 15px 0 rgb(var(--text)),
            -15px 15px 0 rgb(var(--text)),
            15px -15px 0 rgb(var(--text)),
            -15px -15px 0 rgb(var(--text))
          `,
          }}
        />
      </div>
    </button>
  )
}
